import {defineMessages, IntlShape} from 'react-intl';
import {differenceInYears, isValid, parse} from 'date-fns';

export const messages = defineMessages({
  amountRequired: {
    id: 'ideal_inquiry.amount_required',
    defaultMessage: 'Please enter the payment amount.',
  },
  amountTooSmall: {
    id: 'ideal_inquiry.amount_too_small',
    defaultMessage: 'The amount must be greater than or equal to €0.01.',
  },
  amountTooLarge: {
    id: 'ideal_inquiry.amount_too_large',
    defaultMessage: 'The amount is too large.',
  },
  dateRequired: {
    id: 'ideal_inquiry.date_required',
    defaultMessage: 'Please select the payment date.',
  },
  dateFormat: {
    id: 'ideal_inquiry.date_format',
    defaultMessage: 'Please specify a valid payment date.',
  },
  dateTooYoung: {
    id: 'ideal_inquiry.date_future',
    defaultMessage: 'Please enter a date that is today or earlier.',
  },
  dateTooOld: {
    id: 'ideal_inquiry.date_too_old',
    defaultMessage:
      'You can only inquire about iDEAL payments for up to 1 year.',
  },
  ibanRequired: {
    id: 'ideal_inquiry.iban_required',
    defaultMessage: 'Please enter your IBAN number.',
  },
  emailInvalid: {
    id: 'ideal_inquiry.enter_valid_email',
    defaultMessage: 'Please enter a valid email address.',
  },
  messageRequired: {
    id: 'ideal_inquiry.message_required',
    defaultMessage: 'Please enter your message.',
  },
  messageMaxLength: {
    id: 'ideal_inquiry.message_max_length',
    defaultMessage: 'Your message must be less than 500 characters.',
  },
  messageMinLength: {
    id: 'ideal_inquiry.message_min_length',
    defaultMessage: 'Your message must be at least 20 characters.',
  },
});

type FormError = string | undefined;

export const validateAmount = (
  value: number | null,
  intl: IntlShape,
): FormError => {
  let errorMsg: FormError;
  if (value === null) {
    errorMsg = intl.formatMessage(messages.amountRequired);
  } else if (value <= 0) {
    errorMsg = intl.formatMessage(messages.amountTooSmall);
  } else if (value >= 10_000_000_00) {
    errorMsg = intl.formatMessage(messages.amountTooLarge);
  }
  return errorMsg;
};

export const validateIban = (
  value: string | null,
  intl: IntlShape,
): FormError => {
  if (value === null || value === '') {
    return intl.formatMessage(messages.ibanRequired);
  }
  return undefined;
};

export const validatePaymentDate = (
  value: string,
  intl: IntlShape,
): FormError => {
  if (value === '--' || value === '') {
    return intl.formatMessage(messages.dateRequired);
  }

  const date = parse(value, 'yyyy-M-d', new Date());
  if (!isValid(date)) {
    return intl.formatMessage(messages.dateFormat);
  } else if (differenceInYears(new Date(), date) >= 1) {
    return intl.formatMessage(messages.dateTooOld);
  } else if (date > new Date()) {
    return intl.formatMessage(messages.dateTooYoung);
  }

  return undefined;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateEmail = (value: string, intl: IntlShape): FormError => {
  if (value === '') {
    return intl.formatMessage(messages.emailInvalid);
  }
  if (!EMAIL_REGEX.test(value)) {
    return intl.formatMessage(messages.emailInvalid);
  }
  return undefined;
};

export const validateMessage = (value: string, intl: IntlShape): FormError => {
  if (value === '') {
    return intl.formatMessage(messages.messageRequired);
  } else if (value.length > 500) {
    return intl.formatMessage(messages.messageMaxLength);
  } else if (value.length < 20) {
    return intl.formatMessage(messages.messageMinLength);
  }

  return undefined;
};
